import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGlobalContext } from '../../../../context/GlobalContext';
import {
	ICategoriaDTO,
	ICentroCustoDTO,
	IContaDTO,
	IFornecedorDTO,
	ISubCategoriaDTO,
	ITimeLineHistoricoDespesaDTO,
	ITipoPagamentoDTO,
} from '../../../../models/happy-code-api.model';
import { Predicate } from '../../../../models/predicate.model';
import { useCategoriaService } from '../../../../services/categoria.service';
import { useCentroCustoService } from '../../../../services/centro-de-custo.service';
import { useContaService } from '../../../../services/conta.service';
import { useFornecedorService } from '../../../../services/fornecedor.service';
import { useTipoPagamentoService } from '../../../../services/tipo-pagamento.service';
import { useSubCategoriaService } from '../../../../services/subcategoria.service';
import useSaveData from './use-save-and-fetch-data';
import { useDespesaService } from '../../../../services/despesa.service';

export default function useFetchDataDespesaAndState() {
	const [fornecedor, setFornecedor] = useState<IFornecedorDTO[]>();
	const [centroCusto, setCentroCusto] = useState<ICentroCustoDTO[]>();
	const [categoria, setCategoria] = useState<ICategoriaDTO[]>();
	const [subCategoria, setSubCategoria] = useState<ISubCategoriaDTO[]>();
	const [tipoPagamentoList, setTipoPagamentoList] = useState<ITipoPagamentoDTO[]>();
	const [conta, setConta] = useState<IContaDTO[]>();
	const [despesaHistory, setDespesaHistory] = useState<ITimeLineHistoricoDespesaDTO[]>();
	const [despesaObservacaoHistory, setDespesaObservacaoHistory] = useState<ITimeLineHistoricoDespesaDTO[]>();
	const { setIsGlobalLoading, unidade } = useGlobalContext();
	const { idDespesa }: any = useParams();

	const fornecedorService = useFornecedorService();
	const centroCustoService = useCentroCustoService();
	const categoriaService = useCategoriaService();
	const tipoPagamento = useTipoPagamentoService();
	const contaService = useContaService();
	const subCategoriaService = useSubCategoriaService();
	const despesaService = useDespesaService();

	const { fetchDespesaByIdForm, despesa, setDespesa, createOrPatchDespesa } = useSaveData(idDespesa);

	const isEdit = !!idDespesa;

	const fetchFornecedores = () => {
		const predicate = new Predicate();
		predicate.addOption('unidade.id', unidade?.id);
		fornecedorService.findList(predicate).then(({ data }) => setFornecedor(data));
	};
	const fetchCentroCusto = () => {
		const predicate = new Predicate();
		predicate.addOption('ativo', true);
		centroCustoService.findList(predicate).then(({ data }) => setCentroCusto(data));
	};

	const fetchCategorias = () => {
		const predicate = new Predicate();
		predicate.addOption('ativo', true);
		categoriaService.findList(predicate).then(({ data }) => setCategoria(data));
	};

	const fetchSubCategorias = () => {
		const predicate = new Predicate();
		setIsGlobalLoading(true);
		predicate.addOption('categoria.id', despesa?.categoria?.id);
		subCategoriaService
			.findList(predicate)
			.then(({ data }) => setSubCategoria(data))
			.finally(() => setIsGlobalLoading(false));
	};

	const fetchTipoPagamento = () => {
		const predicate = new Predicate();
		predicate.addOption('ativo', true);
		tipoPagamento.findList(predicate).then(({ data }) => setTipoPagamentoList(data));
	};

	const fetchContas = () => {
		const predicate = new Predicate();
		predicate.addOption('unidade.id', unidade?.id);
		predicate.addOption('ativo', true);
		contaService.findList(predicate).then(({ data }) => setConta(data));
	};
	const fetchDespesaHistory = () => {
		despesaService.findHistory(idDespesa).then(({ data }) => setDespesaHistory(data));
	}

	const fetchObservacaoHistory = () => {
		despesaService.findObservacaoHistory(idDespesa).then(({ data }) => setDespesaObservacaoHistory(data));
	};

	useEffect(() => {
		if (!unidade?.id) return;
		setIsGlobalLoading(true);
		if (isEdit) {
			fetchDespesaByIdForm();
			fetchDespesaHistory();
			fetchObservacaoHistory();	
		} else{
			setDespesa({...despesa, dhCompetencia: new Date()});
		}
		Promise.all([fetchFornecedores(), fetchCentroCusto(), fetchCategorias(), fetchTipoPagamento(), fetchContas()]).finally(() => setIsGlobalLoading(false));
	}, [unidade?.id]);

	useEffect(() => {
		if (!despesa?.categoria) return;
		fetchSubCategorias();
	}, [despesa?.categoria]);

	return {
		fornecedor,
		centroCusto,
		categoria,
		subCategoria,
		tipoPagamentoList,
		conta,
		despesa,
		isEdit,
		despesaObservacaoHistory,
		despesaHistory,
		setDespesa,
		createOrPatchDespesa,
		fetchContas,
	};
}
